import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Vector3, Vector4 } from "@babylonjs/core/Maths/math.vector";
import { AdditionGeometry } from './AdditionGeometry';
import { CsgUtils } from "../CsgUtils";
import { GeoUtils } from "./GeoUtils";
import { Util } from "../../utils/Util";
import { WallAddition } from '../WallAddition';
import { PI_DIV2 } from "@egr/wcf/modules/utils/math";
import { PropertyValue, StringProperty, PropertyChangedResult, NumberProperty, Color3Property } from '@egr/wcf/modules/core/prop';
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { convertLengthUnit, LengthUnit } from "@egr/wcf/modules/utils/number";
export class PictureFrame extends AdditionGeometry {
    constructor(pRoomManager, pAddition) {
        super(pRoomManager, pAddition);
        this.mMatOfCatList = new Array();
    }
    get canMirrorV() {
        return (false);
    }
    getType() {
        return ("PictureFrame");
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.75;
        tParams.height = 0.5;
        tParams.depth = 0.025;
        tParams.wallOffset = 0.0;
        tParams.floorOffset = 1.2;
        tParams.color = "#aaaaaa";
        tParams.frameWidth = 0.005;
        return (tParams);
    }
    create2dGeometry(pParent) {
        this.createWallPictureFrame2d(pParent, this.prepare2dMesh);
    }
    create3dGeometry(pParent) {
        this.createWallPictureFrame(pParent);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getProperties() {
        let tProperties = new Array();
        let tColorChoices = new Array();
        for (let tCol of this.mRoomManager._wallColors)
            tColorChoices.push(this.mRoomManager._getWallColorPropValue(tCol));
        //frame color
        let tFrameColorProp = new StringProperty(this.mAddition, PictureFrame.PROP_KEY_FRAMECOLOR, this.mRoomManager.resources.propertyFrameColor);
        tFrameColorProp.choiceList = true;
        tFrameColorProp.getChoices = () => Promise.resolve(tColorChoices);
        tFrameColorProp.getValue = () => this.mRoomManager._getWallColorPropValue(this.mAddition.color);
        tFrameColorProp.setValue = (pValue) => {
            if (pValue != null) {
                let tColor = StringProperty.ConvertValueToString(pValue);
                this.mAddition.setColor(tColor);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        switch (this.mAddition.ImageType) {
            case 3:
                tFrameColorProp.editable = false;
                break;
            default:
                tFrameColorProp.editable = true;
                break;
        }
        tProperties.push(tFrameColorProp);
        // category list
        //let tMaterialCategory = new Array<PropertyValue>();
        let tType = "pictureframe";
        let tsubType = "3_2";
        switch (this.mAddition.ImageType) {
            case 0:
                tsubType = "3_2";
                break;
            case 1:
                tsubType = "2_3";
                break;
            case 2:
                tsubType = "1_1";
                break;
            case 3:
                tsubType = "WB";
                break;
            case 4:
                tsubType = "own";
                break;
            default:
                tsubType = "3_2";
                break;
        }
        let tMaterialCategory = this.mRoomManager.getMaterial_Categories(tType + "_" + this.mAddition.ImageType);
        //material category
        let tMaterialProp = new NumberProperty(this.mAddition, PictureFrame.PROP_KEY_MATERIALCATEGORY, this.mRoomManager.resources.propertyMaterialCategory);
        //tMaterialPropR.class = "wallSide_a";
        tMaterialProp.choiceList = true;
        tMaterialProp.getChoices = () => Promise.resolve(tMaterialCategory);
        tMaterialProp.getValue = () => this.mRoomManager.getMaterialCategoryValue(tMaterialCategory, this.mAddition.materialSet);
        tMaterialProp.setValue = (pValue) => {
            if (pValue != null) {
                this.mAddition.setMaterialCategory(StringProperty.ConvertValueToString(pValue), undefined, this.mAddition.materialSet);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        switch (this.mAddition.ImageType) {
            case 4:
            case 3:
                tMaterialProp.visible = false;
                break;
            default:
                tMaterialProp.visible = true;
                break;
        }
        tProperties.push(tMaterialProp);
        //color/material
        let tpropTitleR = this.mAddition.materialSet.category === "" ? this.mRoomManager.resources.propertyColor :
            this.mRoomManager.resources.propertyMaterial;
        let tColorRProp = new Color3Property(this.mAddition, PictureFrame.PROP_KEY_COLOR, tpropTitleR);
        //tColorRProp.class = "wallSide_a";
        tColorRProp.choiceList = true;
        this.mMatOfCatList = this.mRoomManager.getMaterialsOfCategory(this.mAddition.materialSet, tType);
        tColorRProp.getChoices = () => Promise.resolve(this.mMatOfCatList);
        tColorRProp.getValue = () => this.mRoomManager.getMaterialValue(this.mAddition.materialSet, this.mMatOfCatList);
        tColorRProp.setValue = (pValue) => {
            let tValue = StringProperty.ConvertValueToString(pValue);
            if (tValue != null) {
                this.mAddition.setMaterial(tValue, this.mAddition.materialSet);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        switch (this.mAddition.ImageType) {
            case 4:
            case 3:
                tColorRProp.visible = false;
                break;
            default:
                tColorRProp.visible = true;
                break;
        }
        tProperties.push(tColorRProp);
        //preview image for custom image
        let tmatId = this.mRoomManager.getMaterialId(this.mAddition.materialSet);
        let tmat = this.mMatManager.getMaterial(tmatId);
        if (tmat != null && this.mAddition.ImageType === 4) {
            let tPropValue = new PropertyValue("image", "image");
            let tURL = "";
            if (tmat.albedoTexture != null) {
                tURL = tmat.albedoTexture.url == null ? "" : tmat.albedoTexture.url;
            }
            else {
                tURL = this.mRoomManager.generateColorPreviewImage("#ffffff");
            }
            tPropValue.smallIcon = tURL;
            tPropValue.largeIcon = tURL;
            let tCustomImageProp = new StringProperty(this.mAddition, PictureFrame.PROP_KEY_CUSTOMIMAGE, "image");
            tCustomImageProp.choiceList = true;
            tCustomImageProp.getValue = () => tPropValue;
            tCustomImageProp.setValue = (pValue) => {
                let tValue = StringProperty.ConvertValueToString(pValue);
                if (tValue != null) {
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tProperties.push(tCustomImageProp);
        }
        return (tProperties);
    }
    getEOXDescription(pDocument, pParentNode, pRootNode, pWall) {
        let tAttribsList = new Array();
        tAttribsList = [];
        tAttribsList.push({ qualifiedName: "structure_type", value: "WallPicture" });
        let tRoomElementNode = Util._xmlAddChildNode(pDocument, pRootNode, "object", "", tAttribsList);
        // define category
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "category", "Other", null);
        // position
        tAttribsList = [];
        tAttribsList.push({ qualifiedName: "x", value: this.mAddition.position.x.toString() });
        tAttribsList.push({ qualifiedName: "y", value: "0" });
        tAttribsList.push({ qualifiedName: "z", value: (this.mAddition.position.y).toString() });
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "position", "", tAttribsList);
        // rotation
        let t3dRotation = this.mAddition.node.rotationQuaternion;
        tAttribsList = [];
        if (t3dRotation != null) {
            tAttribsList.push({ qualifiedName: "x", value: (-t3dRotation.x).toString() });
            tAttribsList.push({ qualifiedName: "y", value: (-t3dRotation.z).toString() });
            tAttribsList.push({ qualifiedName: "z", value: (-t3dRotation.y).toString() });
            tAttribsList.push({ qualifiedName: "w", value: (-t3dRotation.w).toString() });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
        else {
            tAttribsList.push({ qualifiedName: "x", value: "0" });
            tAttribsList.push({ qualifiedName: "y", value: "0" });
            tAttribsList.push({ qualifiedName: "z", value: "0" });
            tAttribsList.push({ qualifiedName: "w", value: "1" });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
        // width
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "width", this.mAddition.width.toString(), null);
        // height
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "height", this.mAddition.height.toString(), null);
        // parapet
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "installationHeight", this.mAddition.floorOffset.toString(), null);
        // color
        tAttribsList = [];
        if (this.mAddition.color.trim() != '') {
            let tcolor = Util.hexToRGB(this.mAddition.color);
            tAttribsList.push({ qualifiedName: "r", value: tcolor.r.toString() });
            tAttribsList.push({ qualifiedName: "g", value: tcolor.g.toString() });
            tAttribsList.push({ qualifiedName: "b", value: tcolor.b.toString() });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "color", "", tAttribsList);
        }
    }
    getEOXDescription_objects(pDocument, pRootNode, pWall) {
        let tAttribsList = new Array();
        tAttribsList = [];
        let tRoomElementNode = Util._xmlAddChildNode(pDocument, pRootNode, "object", "", tAttribsList);
        // define category
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "category", "Article", null);
        // define programID
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "programID", "OFFICE2", null);
        // define configuration
        // image_3_2,
        // image_2_3,
        // image_1_1,
        // whiteboard,
        // customImage,
        // chalkboard
        let t_frameType = "32";
        switch (this.mAddition.ImageType) {
            case WallAddition.ImageType.image_3_2:
                Util._xmlAddChildNode(pDocument, tRoomElementNode, "articleNumber", "1000", null);
                t_frameType = "Bilderrahmen_32";
                break;
            case WallAddition.ImageType.image_2_3:
                Util._xmlAddChildNode(pDocument, tRoomElementNode, "articleNumber", "1001", null);
                t_frameType = "Bilderrahmen_23";
                break;
            case WallAddition.ImageType.image_1_1:
                t_frameType = "Bilderrahmen_11";
                break;
            case WallAddition.ImageType.whiteboard:
                Util._xmlAddChildNode(pDocument, tRoomElementNode, "articleNumber", "1005", null);
                t_frameType = "A_1005";
                break;
            case WallAddition.ImageType.chalkboard:
                Util._xmlAddChildNode(pDocument, tRoomElementNode, "articleNumber", "1010", null);
                t_frameType = "A_1005";
                break;
            default:
                t_frameType = "Bilderrahmen_32";
                break;
        }
        let tconfiguration = this.getConfiguration(this.mAddition.ImageType, t_frameType);
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "configuration", tconfiguration, null);
        // position
        let tmoveHX = new Vector3(0, 0, 0);
        if (this.mAddition.wall != null) {
            tmoveHX = Util.to3d(this.mAddition.wall.wallDirection).scale(this.mAddition.width * 0.5);
        }
        let tPos_Y = this.mAddition.node.position.clone();
        tPos_Y.subtractInPlace(tmoveHX);
        tPos_Y.addInPlace(this.mAddition.getGeometryOffset_Z(true));
        tPos_Y.y = this.mAddition.floorOffset;
        tAttribsList = [];
        // tAttribsList.push({qualifiedName: "x", value: (tPos_Y.x - (this.mAddition.width * 0.5)).toString()});
        tAttribsList.push({ qualifiedName: "x", value: (tPos_Y.x).toString() });
        tAttribsList.push({ qualifiedName: "y", value: "0" });
        tAttribsList.push({ qualifiedName: "z", value: (-tPos_Y.z).toString() });
        Util._xmlAddChildNode(pDocument, tRoomElementNode, "position", "", tAttribsList);
        // rotation
        let t3dRotation = this.mAddition.node.absoluteRotationQuaternion;
        tAttribsList = [];
        if (t3dRotation != null) {
            tAttribsList.push({ qualifiedName: "x", value: (t3dRotation.x).toString() });
            tAttribsList.push({ qualifiedName: "y", value: (-t3dRotation.y).toString() });
            tAttribsList.push({ qualifiedName: "z", value: (t3dRotation.z).toString() });
            tAttribsList.push({ qualifiedName: "w", value: (t3dRotation.w).toString() });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
        else {
            tAttribsList.push({ qualifiedName: "x", value: "0" });
            tAttribsList.push({ qualifiedName: "y", value: "0" });
            tAttribsList.push({ qualifiedName: "z", value: "0" });
            tAttribsList.push({ qualifiedName: "w", value: "1" });
            Util._xmlAddChildNode(pDocument, tRoomElementNode, "rotation", "", tAttribsList);
        }
    }
    createWallPictureFrame(pParent) {
        let tW = this.mAddition.width;
        let tH = this.mAddition.height;
        let tD = this.mAddition.depth;
        let tWO = this.mAddition.wallOffset;
        let tWT = this.mAddition.WallThickness;
        //this.mRoomManager.createColorMaterial(this.mAddition.color);
        this.mRoomManager.createColorMaterial(this.mAddition.color);
        let tMatId = "color" + this.mRoomManager.materialSplitStr + this.mAddition.color;
        let tBackPlane = CreatePlane("room:wallPicture:backPlane", {
            width: tW,
            height: tH
        }, this.mScene);
        if (tBackPlane != null) {
            tBackPlane.parent = pParent;
            tBackPlane.position.z = (-tWT * 0.5) - tWO - 0.001;
            tBackPlane.position.y = tH * 0.5;
            tBackPlane.isPickable = true;
            tBackPlane.material = this.mRoomManager.MatManager.getMaterial(tMatId);
            if (this.mAddition.mirrorHorizontal) {
                tBackPlane.position.z = -tBackPlane.position.z;
            }
            this.prepare3dMesh(tBackPlane);
        }
        let tFrame = GeoUtils.createFrame(tW, tH, tD, this.mAddition.frameWidth, pParent, this.mScene);
        if (tFrame != null) {
            tFrame.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
            tFrame.position.y = tH * 0.5;
            tFrame.material = this.mRoomManager.MatManager.getMaterial(tMatId);
            if (this.mAddition.wall != null)
                tFrame.name = "room:wall|" + this.mAddition.wall.id.toString();
            if (this.mAddition.mirrorHorizontal) {
                tFrame.position.z = -tFrame.position.z;
            }
            this.prepare3dMesh(tFrame);
        }
        if (this.mAddition.ImageType != 5)
            this.createWallPicture_Plane(pParent);
        else
            this.createWallPicture_CSG(pParent);
    }
    createWallPictureFrame2d(pParent, callback) {
        let tHW = (this.mAddition.width * 0.5);
        let tHD = (this.mAddition.depth * 0.5);
        let tD = this.mAddition.depth;
        let tWT = this.mAddition.WallThickness;
        let tWO = this.mAddition.wallOffset;
        // shape
        let tPlane_PictureFrame = CreatePlane("", { width: tHW * 2, height: tD }, this.mScene);
        tPlane_PictureFrame.rotation.x = PI_DIV2;
        tPlane_PictureFrame.parent = pParent;
        tPlane_PictureFrame.material = this.mRoomManager.app.model.materials.defaultMaterial;
        tPlane_PictureFrame.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tPlane_PictureFrame.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tPlane_PictureFrame.isPickable = true;
        // outline
        let tLines = CreateLines("", {
            points: [
                new Vector3(-tHW, 0.0, -tHD),
                new Vector3(-tHW, 0.0, tHD),
                new Vector3(tHW, 0.0, tHD),
                new Vector3(tHW, 0.0, -tHD),
                new Vector3(-tHW, 0.0, -tHD)
            ],
            updatable: false,
            instance: null
        }, this.mScene);
        tLines.isPickable = true;
        tLines.intersectionThreshold = 0;
        tLines.color = Color3.Black();
        tLines.position.y = -pParent.position.y + WallAddition.WALLADDITION_2D_LINE_HEIGHT;
        tLines.position.z = -tWT * 0.5 - tD * 0.5 - tWO;
        tLines.parent = pParent;
        tLines.renderingGroupId = 0;
        tLines.freezeWorldMatrix();
        if (this.mAddition.mirrorHorizontal) {
            tPlane_PictureFrame.position.z = -tPlane_PictureFrame.position.z;
            tLines.position.z = -tLines.position.z;
        }
        ShadowGenerator.disableMeshShadow(tPlane_PictureFrame);
        ShadowGenerator.disableMeshShadow(tLines);
        callback(tPlane_PictureFrame);
        callback(tLines);
    }
    createWallPicture_Plane(pParent) {
        let tW = this.mAddition.width;
        let tH = this.mAddition.height;
        let tD = this.mAddition.depth;
        let tWO = this.mAddition.wallOffset;
        let tWT = this.mAddition.WallThickness;
        this.mRoomManager.createColorMaterial(this.mAddition.color);
        let tfUV = new Vector4(0, 0, 1, 1); // front image = half the whole image along the width
        let tWallPicture = CreatePlane("room:wallPicture:imagePlane", {
            width: tW - (this.mAddition.frameWidth * 2),
            height: tH - (this.mAddition.frameWidth * 2),
            sideOrientation: Mesh.FRONTSIDE,
            frontUVs: tfUV
        }, this.mScene);
        if (tWallPicture === null)
            return;
        tWallPicture.position.z = -tWT * 0.5 - tD - tWO + 0.003;
        if (this.mAddition.mirrorHorizontal) {
            tWallPicture.position.z = -tWallPicture.position.z;
            tWallPicture.rotation.x = Math.PI;
            tWallPicture.rotation.z = Math.PI;
        }
        this.mAddition.setGeometryOffset_Z(tWallPicture.position.z);
        let tMaterialId = this.mAddition.getMaterialId(this.mAddition.materialSet);
        tWallPicture.isPickable = true;
        tWallPicture.position.y = tH * 0.5;
        tWallPicture.material = this.mRoomManager.MatManager.getMaterial(tMaterialId);
        tWallPicture.parent = pParent;
        this.prepare3dMesh(tWallPicture);
    }
    createWallPicture_CSG(pParent) {
        let tW = this.mAddition.width;
        let tH = this.mAddition.height;
        let tD = this.mAddition.depth;
        let tWO = this.mAddition.wallOffset;
        let tWT = this.mAddition.WallThickness;
        let tPoints = new Array();
        tPoints.push(new CSG.Vector2D(-tW, 0));
        tPoints.push(new CSG.Vector2D(-tW, -tH));
        tPoints.push(new CSG.Vector2D(0, -tH));
        tPoints.push(new CSG.Vector2D(0, 0));
        let tCAGshape = CAG.fromPointsNoCheck(tPoints);
        if (!tCAGshape.isSelfIntersecting()) {
            let tCSG = Util.CAG2CSG(tCAGshape, 0);
            let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
            if (tMeshes == null)
                return;
            let tMaterialId = this.mAddition.getMaterialId(this.mAddition.materialSet);
            for (let tMesh of tMeshes) {
                tMesh.parent = pParent;
                tMesh.name = "room:wallPicture:imagePlane";
                tMesh.rotation.x = Math.PI / 2;
                tMesh.position.z = -tWT * 0.5 - tD - tWO + 0.003;
                if (this.mAddition.mirrorHorizontal) {
                    tMesh.position.z = -tMesh.position.z - tWO;
                }
                tMesh.scaling.z = -1;
                tMesh.position.y += tH;
                tMesh.position.x += tW * 0.5;
                tMesh.material = this.mRoomManager.MatManager.getMaterial(tMaterialId);
                this.mAddition.setGeometryOffset_Z(tMesh.position.z);
                if (tMesh.material != null)
                    tMesh.material.backFaceCulling = false;
            }
            this.prepare3dMesh(tMeshes[0]);
        }
    }
    getConfiguration(pImageType, pFrameType) {
        let t_height = convertLengthUnit(this.mAddition.height, LengthUnit.Meter, LengthUnit.Millimeter);
        let t_width = convertLengthUnit(this.mAddition.width, LengthUnit.Meter, LengthUnit.Millimeter);
        let t_frameWidth = convertLengthUnit(this.mAddition.frameWidth, LengthUnit.Meter, LengthUnit.Millimeter).toString();
        t_frameWidth = Util.leadingZero(t_frameWidth, 3);
        let t_insertionHeight = convertLengthUnit(this.mAddition.floorOffset, LengthUnit.Meter, LengthUnit.Millimeter);
        let t_motiv = this.mAddition.materialSet.value;
        let tconfiguration = "";
        switch (pImageType) {
            case WallAddition.ImageType.image_3_2:
                tconfiguration = pFrameType + ".breite=" + t_width + ";" +
                    pFrameType + ".tiefe=25;" +
                    pFrameType + ".breiterahmen=" + t_frameWidth + ";" +
                    pFrameType + ".motiv=" + t_motiv + ";" +
                    "INSERT_HEIGHT.INSERTHEIGHT=" + t_insertionHeight;
                break;
            case WallAddition.ImageType.image_2_3:
                tconfiguration = pFrameType + ".hoehe=" + t_height + ";" +
                    pFrameType + ".tiefe=25;" +
                    pFrameType + ".breiterahmen=" + t_frameWidth + ";" +
                    pFrameType + ".motiv=" + t_motiv + ";" +
                    "INSERT_HEIGHT.INSERTHEIGHT=" + t_insertionHeight;
                break;
            case WallAddition.ImageType.whiteboard:
                tconfiguration = pFrameType + ".breite=" + t_width + ";" +
                    pFrameType + ".hoehe=" + t_height + ";" +
                    pFrameType + ".tiefe=25;" +
                    pFrameType + ".breiterahmen=" + t_frameWidth + ";" +
                    "INSERT_HEIGHT.INSERTHEIGHT=" + t_insertionHeight;
                break;
            case WallAddition.ImageType.chalkboard:
                tconfiguration = pFrameType + ".breite=" + t_width + ";" +
                    pFrameType + ".hoehe=" + t_height + ";" +
                    pFrameType + ".tiefe=25;" +
                    pFrameType + ".breiterahmen=" + t_frameWidth + ";" +
                    "INSERT_HEIGHT.INSERTHEIGHT=" + t_insertionHeight;
                break;
            default:
                tconfiguration = "";
                break;
        }
        return (tconfiguration);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
PictureFrame.PROP_KEY_FRAMECOLOR = "room:WallAdditon:framecolor";
PictureFrame.PROP_KEY_COLOR = "room:WallAdditon:color";
PictureFrame.PROP_KEY_CUSTOMIMAGE = "room:WallAdditon:customImage";
PictureFrame.PROP_KEY_MATERIALCATEGORY = "room:WallAddition:materialCategory";
